import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { fieldLabel } from 'src/services/whatsapp/bindings'
import { bindingSourceEnum, bindingSourceFields } from 'src/services/whatsapp/models'
import { DraftBinding } from 'src/services/whatsapp/use-template-bindings'
import { InfoHint } from 'src/components/info-hint'

interface TemplateBindingsEditorProps {
  variables: string[]
  bindings: Record<string, DraftBinding>
  onSourceChange: (variable: string, source: string) => void
  onFieldChange: (variable: string, field: string) => void
}

/**
 * Authors the per-variable source/field mapping for a template body. Shared
 * by the create and edit (rejected-template-edit-resubmit) forms, so a
 * template-variable-picker upgrade to the body input updates both at once.
 */
export const TemplateBindingsEditor: FC<TemplateBindingsEditorProps> = ({
  variables,
  bindings,
  onSourceChange,
  onFieldChange,
}) => {
  const { t } = useTranslation()
  if (variables.length === 0) return null

  const sourceOptions = [
    { value: bindingSourceEnum.CONTACT, label: t('whatsappTemplates.sourceContact') },
    { value: bindingSourceEnum.RESERVATION, label: t('whatsappTemplates.sourceReservation') },
    { value: bindingSourceEnum.GUEST, label: t('whatsappTemplates.sourceGuest') },
    { value: bindingSourceEnum.ORGANIZATION, label: t('whatsappTemplates.sourceOrganization') },
    { value: bindingSourceEnum.RESIDENCE, label: t('whatsappTemplates.sourceProperty') },
    { value: bindingSourceEnum.MANUAL, label: t('whatsappTemplates.sourceManual') },
  ]

  return (
    <div className="space-y-2" data-testid="variable-bindings">
      <p className="text-sm font-medium text-text-primary">
        {t('whatsappTemplates.bindingsLabel')}
        <InfoHint text={t('whatsappTemplates.bindingsHint')} className="ml-1.5" />
      </p>
      {variables.map((variable) => {
        const binding = bindings[variable]
        const source = binding?.source ?? ''
        const fields =
          source in bindingSourceFields
            ? bindingSourceFields[source as keyof typeof bindingSourceFields]
            : []
        return (
          <div
            key={variable}
            className="flex flex-wrap items-center gap-2"
            data-testid={`binding-row-${variable}`}
          >
            <span className="min-w-32 rounded bg-primary-50 px-2 py-1 font-mono text-sm text-primary">
              {`{{${variable}}}`}
            </span>
            <select
              value={source}
              onChange={(e) => onSourceChange(variable, e.target.value)}
              data-testid={`binding-source-${variable}`}
              className="rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-text-primary"
            >
              <option value="" disabled>
                {t('whatsappTemplates.selectSource')}
              </option>
              {sourceOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {fields.length > 0 && (
              <select
                value={binding?.field ?? ''}
                onChange={(e) => onFieldChange(variable, e.target.value)}
                data-testid={`binding-field-${variable}`}
                className="rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-text-primary"
              >
                {fields.map((field) => (
                  <option key={field} value={field}>
                    {fieldLabel(field, t)}
                  </option>
                ))}
              </select>
            )}
          </div>
        )
      })}
    </div>
  )
}
